import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      
      // Fetch VAPID public key from server
      axios.get(`${API}/push/vapid-key`)
        .then(res => {
          setVapidPublicKey(res.data.publicKey);
        })
        .catch(err => console.error('Failed to get VAPID key:', err));
      
      // Register service worker
      navigator.serviceWorker.register('/sw-push.js')
        .then(reg => {
          setRegistration(reg);
          return reg.pushManager.getSubscription();
        })
        .then(sub => {
          setSubscription(sub);
        })
        .catch(err => {
          console.error('Service worker registration failed:', err);
        });
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      return { success: false, error: 'Push notifications not supported' };
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result !== 'granted') {
        return { success: false, error: 'Permission denied' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    if (!isSupported || !registration || !vapidPublicKey) {
      return { success: false, error: 'Not supported or not ready' };
    }

    if (permission !== 'granted') {
      const permResult = await requestPermission();
      if (!permResult.success) {
        return permResult;
      }
    }

    try {
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      setSubscription(sub);

      // Send to backend
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/push/subscribe`, {
        subscription: sub.toJSON()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return { success: true, subscription: sub };
    } catch (error) {
      console.error('Subscription failed:', error);
      return { success: false, error: error.message };
    }
  }, [isSupported, registration, permission, requestPermission]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) {
      return { success: true };
    }

    try {
      await subscription.unsubscribe();
      setSubscription(null);

      // Remove from backend
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/push/subscribe`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return { success: true };
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      return { success: false, error: error.message };
    }
  }, [subscription]);

  const scheduleGameDayAlerts = useCallback(async (gameDate, gameTime, alerts) => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/gameday/schedule-alerts`, {
        game_date: gameDate,
        game_time: gameTime,
        alerts: alerts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Failed to schedule alerts:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const getScheduledAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/gameday/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, alerts: response.data.alerts };
    } catch (error) {
      return { success: false, error: error.message, alerts: [] };
    }
  }, []);

  const cancelAlerts = useCallback(async (gameDate) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/gameday/alerts/${gameDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  return {
    isSupported,
    permission,
    subscription,
    isSubscribed: !!subscription,
    requestPermission,
    subscribe,
    unsubscribe,
    scheduleGameDayAlerts,
    getScheduledAlerts,
    cancelAlerts
  };
}

export default usePushNotifications;
